import React, { useState, useEffect } from 'react'
import {
    Modal,
    Form,
    Button,
    Typography,
    message,
    Input,
    Checkbox,
    Radio,
    Divider
} from 'antd'
import { ChadhavaOrderV2, ChildOrder, SkuItem } from '@a4b/api/src/modules/Monetisation/Offerings/types'
import { CopyOutlined } from '@ant-design/icons'

const { Text, Title } = Typography
const { TextArea } = Input

interface SkuRefundItem {
    id: string
    name: string
    amount: number
    refundType: 'none' | 'partial' | 'full'
    partialAmount?: number
}

interface PartialRefundModalChadhavaProps {
    visible: boolean
    onCancel: () => void
    onConfirm: (data: PartialRefundFormData) => void
    order: ChadhavaOrderV2 | null
    childOrder: ChildOrder | null
    loading?: boolean
}

export interface PartialRefundFormData {
    order_id: string
    child_order_id: string
    skus: Array<{
        inventory_id: string
        amount: number
    }>
    amount: number
    remarks: string
    type: 'partial'
    is_delivery_charge_available: boolean
    is_other_charge_available: boolean
    include_all_charges: boolean
    delivery_charges: boolean
}

const PartialRefundModalChadhava: React.FC<PartialRefundModalChadhavaProps> = ({
    visible,
    onCancel,
    onConfirm,
    order,
    childOrder,
    loading = false
}) => {
    const [form] = Form.useForm()
    const [skuItems, setSkuItems] = useState<SkuRefundItem[]>([])
    const [includeAllCharges, setIncludeAllCharges] = useState<boolean>(false)
    const [deliveryCharges, setDeliveryCharges] = useState<boolean>(false)
    const [isSkuModified, setIsSkuModified] = useState<boolean>(false)

    // Get currency symbol based on country code
    const getCurrencySymbol = (countryCode?: string) => {
        return countryCode === 'US' ? '$' : '₹'
    }

    useEffect(() => {
        if (visible && childOrder) {
            // Initialize SKU items from child order
            const initialSkuItems: SkuRefundItem[] = childOrder.sku_name?.map((sku: SkuItem) => ({
                id: sku.id,
                name: sku.name,
                amount: sku.amount,
                refundType: 'none' as const,
                partialAmount: 0
            })) || []

            // If no SKU items but child order has amount, create a default item
            if (initialSkuItems.length === 0 && childOrder.amount > 0) {
                initialSkuItems.push({
                    id: 'child-order-amount',
                    name: 'Order Amount',
                    amount: childOrder.amount,
                    refundType: 'none' as const,
                    partialAmount: 0
                })
            }

            setSkuItems(initialSkuItems)

            // Set initial charge states based on child order
            setIncludeAllCharges(childOrder.is_other_charge_available || false)
            setDeliveryCharges(childOrder.is_delivery_charge_available || false)

            // Reset modification flag
            setIsSkuModified(false)

            form.setFieldsValue({
                remarks: ''
            })
        }
    }, [visible, childOrder, form])

    const handleSkuRefundTypeChange = (skuId: string, refundType: 'none' | 'partial' | 'full') => {
        const updatedSkuItems = skuItems.map(item => {
            if (item.id === skuId) {
                return {
                    ...item,
                    refundType,
                    partialAmount: refundType === 'full' ? item.amount : refundType === 'none' ? 0 : item.partialAmount
                }
            }
            return item
        })
        setSkuItems(updatedSkuItems)
        setIsSkuModified(true)
    }

    const handlePartialAmountChange = (skuId: string, amount: number) => {
        const updatedSkuItems = skuItems.map(item => {
            if (item.id === skuId) {
                return {
                    ...item,
                    partialAmount: amount
                }
            }
            return item
        })
        setSkuItems(updatedSkuItems)
        setIsSkuModified(true)
    }

    const handleChargesChange = (type: 'includeAll' | 'delivery', checked: boolean) => {
        if (type === 'includeAll') {
            setIncludeAllCharges(checked)
        } else {
            setDeliveryCharges(checked)
        }
    }

    const calculateTotalRefundAmount = () => {
        // If no SKU items, use the child order amount directly
        if (skuItems.length === 0 && childOrder) {
            return childOrder.amount
        }

        const allSkusNone = skuItems.every(item => item.refundType === 'none')
        if (allSkusNone) {
            return 0
        }

        const allSkusFullRefund = skuItems.every(item => item.refundType === 'full' || item.refundType === 'none')
        const hasAnyFullRefund = skuItems.some(item => item.refundType === 'full')

        if (allSkusFullRefund && hasAnyFullRefund && childOrder) {
            const fullRefundTotal = skuItems
                .filter(item => item.refundType === 'full')
                .reduce((sum, item) => sum + item.amount, 0)
            return fullRefundTotal
        }

        return skuItems.reduce((sum, item) => {
            if (item.refundType === 'none') {
                return sum
            }
            const refundAmount = item.refundType === 'full' ? item.amount : (item.partialAmount || 0)
            return sum + refundAmount
        }, 0)
    }

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()

            // Prepare SKUs with refund amounts - only include items that are not 'none'
            const skus = skuItems
                .filter(item => item.refundType !== 'none') // Exclude items with 'none' type
                .filter(item => item.refundType === 'partial' ? item.partialAmount && item.partialAmount > 0 : true)
                .filter(item => item.id !== 'child-order-amount') // Exclude the default order amount item
                .map(item => ({
                    inventory_id: item.id,
                    amount: item.refundType === 'full' ? item.amount : (item.partialAmount || 0)
                }))

            const totalRefundAmount = calculateTotalRefundAmount()

            const refundData: PartialRefundFormData = {
                order_id: childOrder?.id || '',
                child_order_id: childOrder?.id || '',
                skus,
                amount: totalRefundAmount,
                remarks: values.remarks || '',
                type: 'partial',
                is_delivery_charge_available: deliveryCharges,
                is_other_charge_available: includeAllCharges,
                include_all_charges: includeAllCharges,
                delivery_charges: deliveryCharges
            }
            onConfirm(refundData)
        } catch (error) {
            console.error('Form validation failed:', error)
        }
    }

    const copyOrderId = () => {
        const orderId = childOrder?.id || order?.id
        if (orderId) {
            navigator.clipboard.writeText(orderId)
            message.success('Order ID copied to clipboard')
        }
    }

    // Don't render modal if required data is null
    if (!order || !childOrder) {
        return null
    }

    return (
        <Modal
            title={null}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width={800}
            centered
        >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_6877_25503)">
                        <path d="M20 0C8.95536 0 0 8.95536 0 20C0 31.0446 8.95536 40 20 40C31.0446 40 40 31.0446 40 20C40 8.95536 31.0446 0 20 0ZM20 36.6071C10.8304 36.6071 3.39286 29.1696 3.39286 20C3.39286 10.8304 10.8304 3.39286 20 3.39286C29.1696 3.39286 36.6071 10.8304 36.6071 20C36.6071 29.1696 29.1696 36.6071 20 36.6071Z" fill="#1890FF" />
                        <path d="M17.8564 27.8571C17.8564 28.4255 18.0822 28.9705 18.4841 29.3724C18.8859 29.7742 19.431 30 19.9993 30C20.5676 30 21.1127 29.7742 21.5145 29.3724C21.9164 28.9705 22.1422 28.4255 22.1422 27.8571C22.1422 27.2888 21.9164 26.7438 21.5145 26.3419C21.1127 25.9401 20.5676 25.7143 19.9993 25.7143C19.431 25.7143 18.8859 25.9401 18.4841 26.3419C18.0822 26.7438 17.8564 27.2888 17.8564 27.8571V27.8571ZM18.9279 22.8571H21.0707C21.2672 22.8571 21.4279 22.6964 21.4279 22.5V10.3571C21.4279 10.1607 21.2672 10 21.0707 10H18.9279C18.7314 10 18.5707 10.1607 18.5707 10.3571V22.5C18.5707 22.6964 18.7314 22.8571 18.9279 22.8571Z" fill="#1890FF" />
                    </g>
                    <defs>
                        <clipPath id="clip0_6877_25503">
                            <rect width="40" height="40" fill="white" />
                        </clipPath>
                    </defs>
                </svg>
                <Title level={3} style={{ margin: 0 }}>
                    Are You Sure?
                </Title>
            </div>

            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 16px',
                backgroundColor: '#f5f5f5',
                borderRadius: 8,
                marginBottom: 16
            }}>
                <Text strong>Order ID - {childOrder.id}</Text>
                <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={copyOrderId}
                    size="small"
                />
            </div>

            {(childOrder.is_other_charge_available || childOrder.is_delivery_charge_available) && (
                <div style={{ marginBottom: 20 }}>
                    {childOrder.is_other_charge_available && (
                        <Checkbox
                            checked={includeAllCharges}
                            onChange={(e) => handleChargesChange('includeAll', e.target.checked)}
                            style={{
                                marginRight: 24,
                                fontSize: 16,
                                color: '#262626'
                            }}
                        >
                            <span style={{ fontSize: 16 }}>Include all charges</span>
                        </Checkbox>
                    )}
                    {childOrder.is_delivery_charge_available && (
                        <Checkbox
                            checked={deliveryCharges}
                            onChange={(e) => handleChargesChange('delivery', e.target.checked)}
                            style={{
                                fontSize: 16,
                                color: '#262626'
                            }}
                        >
                            <span style={{ fontSize: 16 }}>Delivery charges</span>
                        </Checkbox>
                    )}
                </div>
            )}

            <div style={{
                padding: '16px 20px',
                backgroundColor: '#f9f9f9',
                borderRadius: 8,
                marginBottom: 32
            }}>
                <Text style={{ fontSize: 16, color: '#595959' }}>Total Refund Amount: </Text>
                <Text strong style={{ fontSize: 16, color: '#262626' }}>
                    {getCurrencySymbol(order?.country_code)}{calculateTotalRefundAmount()}
                </Text>
            </div>

            <Form form={form} layout="vertical" style={{ marginBottom: 24 }}>
                <Form.Item
                    label="Remarks"
                    name="remarks"
                    rules={[{ required: true, message: 'Please enter remarks for the refund' }]}
                >
                    <TextArea
                        rows={2}
                        placeholder="Please enter remarks for the refund"
                        style={{ borderRadius: 8 }}
                    />
                </Form.Item>
            </Form>

            {skuItems.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <Title level={4} style={{
                        marginBottom: 24,
                        fontSize: 18,
                        fontWeight: 600,
                        color: '#262626'
                    }}>
                        Refund Breakdown
                    </Title>

                    {skuItems.map((sku, index) => (
                        <div key={sku.id} style={{ marginBottom: 24 }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                padding: '16px 0',
                                gap: 20
                            }}>
                                <div style={{
                                    flex: '0 0 50%',
                                    maxWidth: '50%',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word'
                                }}>
                                    <Text strong style={{ fontSize: 16, wordBreak: 'break-word' }}>{sku.name}</Text>
                                    <br />
                                    <Text strong style={{ fontSize: 16 }}>{getCurrencySymbol(order?.country_code)}{sku.amount}</Text>
                                </div>

                                <div style={{
                                    flex: '0 0 50%',
                                    maxWidth: '50%'
                                }}>
                                    <div style={{ marginBottom: 12 }}>
                                        <Radio.Group
                                            value={sku.refundType}
                                            onChange={(e) => handleSkuRefundTypeChange(sku.id, e.target.value)}
                                        >
                                            <Radio value="partial">Partial Refund</Radio>
                                            <Radio value="full">Full Refund</Radio>
                                            <Radio value="none">None</Radio>
                                        </Radio.Group>
                                    </div>

                                    {sku.refundType === 'partial' && (
                                        <div>
                                            {/* <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                marginBottom: 8
                                            }}>
                                                <Text style={{ fontSize: 14, color: '#ff4d4f' }}>* Enter Partial Amount</Text>
                                            </div> */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                marginTop: 8
                                            }}>
                                                <Text style={{ fontSize: 16 }}>{getCurrencySymbol(order?.country_code)}</Text>
                                                <Input
                                                    type="number"
                                                    value={sku.partialAmount}
                                                    onChange={(e) => handlePartialAmountChange(sku.id, parseFloat(e.target.value) || 0)}
                                                    style={{
                                                        width: "100%",
                                                        height: 40,
                                                        fontSize: 16,
                                                        borderRadius: 8
                                                    }}
                                                    placeholder="100"
                                                    max={sku.amount}
                                                    min={0}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {index < skuItems.length - 1 && (
                                <Divider style={{
                                    margin: '0',
                                    borderColor: '#f0f0f0'
                                }} />
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'right' }}>
                <Button size="large" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    size="large"
                    onClick={handleSubmit}
                    loading={loading}
                // style={{ backgroundColor: '#531DAB', borderColor: '#531DAB' }}
                >
                    Proceed with Refund
                </Button>
            </div>
        </Modal>
    )
}

export default PartialRefundModalChadhava